import React, { useEffect, useMemo, useRef, useContext, useCallback, useReducer, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import { isEqual } from 'lodash'
import moment from 'moment'

import {
    AssetClass,
    augmentIncomingMedia,
    buildSetupPayload,
    campaignKeywords,
    generativeCreativeField,
    isCampaignOpen,
    isCampaignPublished,
    isCampaignRunning,
    prepareCreativePayload,
    tcDisplayName,
    validateCreative
} from '../../../../api/models'

import { actions as creativesDispatchActions, SetupContext, CreativesDispatchContext } from './CampaignSetupProvider'
import { allFilesUploaded, fileStatus } from '../Media/media'

import Input from '../../../Shared/Input'
import Textarea from '../../../Shared/Textarea'
import InfoBox from '../../../Shared/InfoBox'
import ErrorMessage from '../../../Shared/ErrorMessage'
import LoadingSpinner from '../../../Shared/LoadingSpinner'
import DropDown from '../../../Shared/DropDown'

import { arrayReducer, FAIL, initStateArray, initStateObject, objectReducer, OVERWRITE, UPDATE } from '../../../../reducers/default'
import constants from '../../../../constants'
import { hasRestrictedAccessFor, snakeCaseToPascalCase } from '../../../../utils/helpers'
import { creativeFormReducer, creativeActions } from './creativeFormReducer'
import { updateSetupProcess } from '../../../../api/setupProcesses'
import { campaignsSetup, fetchCampaignCtasAsync } from '../../../../api/campaigns'
import AutoFill from './AutoFill'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import ReportableTopic from './ReportableTopic'
import { getUserReports } from 'api/userReports'
import MediaUpload from '../Media/MediaUpload'
import CoCreate from './CoCreate'
import SearchDropdownMultiSelect from './SearchDropdownMultiSelect'
import { fetchAudiences } from 'api/integrations'
import { getErrorText } from './adAccountHelper'

function Keywords({ eventId, setupProcessId, campaign, reportedKeywords = [], keywords = [], isCampaignRunning, className }) {
    const { t } = useTranslation()
    const topics = useMemo(() => {
        return keywords.map(keyword => {
            return {
                is_reported: reportedKeywords.some(reportedKeyword => reportedKeyword === keyword),
                keyword
            }
        })
    }, [reportedKeywords, keywords])

    if (!keywords.length || isCampaignRunning) return null

    return (
        <>
            <div
                className={classnames({'campaign-form__tips col-12 d-xl-none': true, [className]: className })}
            >
                <div className='display-flex'>
                    <InfoBox content="Tips.Recommendations.topicRecommendationCommonTooltip" classNames="d-flex">
                        <label>{t('Recommendations.campaign.setup.TopicRecommendations')}</label>
                    </InfoBox>
                </div>
                <ul className='d-flex flex-wrap mt-1'>
                    {topics.map((topic, index) => (
                        <ReportableTopic eventId={eventId} setupProcessId={setupProcessId} campaign={campaign} key={index} topic={topic} index={index} charLimit={30} />
                    ))}
                </ul>
            </div>
            <div
                className={`campaign-form__tips col-5 d-none d-xl-block ${className ? className : ''}`}
            >
                <div className='display-flex'>
                    <InfoBox content="Tips.Recommendations.topicRecommendationCommonTooltip" classNames='d-flex'>
                        <label>{t('Recommendations.campaign.setup.TopicRecommendations')}</label>
                    </InfoBox>
                </div>
                <ul className='d-flex flex-wrap mt-1'>
                    {topics.map((topic, index) => {
                        const trimmedKeyword = topic.keyword?.trim()
                        if (trimmedKeyword === '' || !trimmedKeyword) return null
                        return (
                            <ReportableTopic eventId={eventId} setupProcessId={setupProcessId} campaign={campaign} key={index} topic={topic} index={index} charLimit={40} />
                        )
                    })}
                </ul>
            </div>
        </>
    )
}

Keywords.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    keywords: PropTypes.array,
    reportedKeywords: PropTypes.array,
    isCampaignRunning: PropTypes.bool,
    className: PropTypes.string,
    setupProcessId: PropTypes.number,
    campaign: PropTypes.object
}

function ArrayField({
    fieldName,
    as = Input,
    inputChildren,
    form,
    onChange,
    children,
    isAddingEnabled = false,
    isRemovingEnabled = true,
    removeBtnClassNames = '',
    minCount = 3,
    maxCount = 5,
    tooltip = null,
    restFn,
    ...rest
}) {
    const { t } = useTranslation()

    const field = form[fieldName]
    if (!Array.isArray(field)) return null

    if (field.filter(Boolean).length < minCount) {
        const fieldsToAdd = minCount - field.length
        for (let index = 0; index < fieldsToAdd; index++) {
            field.push('')
        }
    }

    const Component = as

    const addTitle = isAddingEnabled
        ?
        field.length >= maxCount
            ? ''
            : t('Recommendations.campaign.setup.add')
        : ''

    return (
        <div className='creative-composer-section-wrapper'>
            {tooltip
                ?
                <InfoBox title={tooltip.title} body={tooltip.body}>
                    <label className="fs-18 d-inline-block"> {t(`Recommendations.campaign.setup.${fieldName}.title`)}</label>
                </InfoBox>
                :
                <label className='fs-18'>{t(`Recommendations.campaign.setup.${fieldName}.title`)}</label>
            }
            {children}
            {field.map((value, index) => (
                <Component
                    id={`${fieldName}-${index}`}
                    key={index}
                    placeholder={t(`Recommendations.campaign.setup.${fieldName}.placeholder`) + ` ${index + 1}`}
                    value={value}
                    name={`${fieldName}[]`}
                    onChange={(e) => onChange(e.target.value, index)}
                    showMax={true}
                    {...(restFn ? restFn(index) : {})}
                    {...rest}
                >
                    {inputChildren && inputChildren(index)}
                    {isRemovingEnabled && index > (minCount - 1) && (
                        <button
                            className={classnames({ 'input__remove icon icon-bin': true, [removeBtnClassNames]: true })}
                            type="button"
                            onClick={() => onChange(null, index)}
                            aria-label={t(`Recommendations.campaign.setup.${fieldName}.remove`)}
                        ></button>
                    )}
                </Component>
            ))}
            {!isAddingEnabled ? <div className='pb-4' /> :
                <div className='add-button-alignment-wrapper'>
                    <div className="limited-field-wrapper">
                        {isRemovingEnabled && <button
                            type="button"
                            onClick={() => onChange('', field.length)}
                            disabled={field.length >= maxCount}
                            className="campaign-form__add"
                            aria-label={t(`Recommendations.campaign.setup.${fieldName}.add`)}
                            title={addTitle}
                        >
                            <span>+</span>
                            {t('Recommendations.campaign.setup.add')}
                        </button>}
                        {isAddingEnabled && field.length >= maxCount &&
                            <div className="limit-tooltip">
                                <h5 className="font-weight-bold">{t('Tips.Recommendations.Limit.title')}</h5>
                                {t(`Tips.Recommendations.Limit.${fieldName}`)}
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    )
}

ArrayField.propTypes = {
    fieldName: PropTypes.string.isRequired,
    form: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    isAddingEnabled: PropTypes.bool,
    isRemovingEnabled: PropTypes.bool,
    removeBtnClassNames: PropTypes.string,
    children: PropTypes.node,
    inputChildren: PropTypes.func,
    as: PropTypes.func,
    minCount: PropTypes.number,
    maxCount: PropTypes.number,
    tooltip: PropTypes.object,
    restFn: PropTypes.func,
}

function Field({ fieldName, as = Input, onChange, tooltip, isRequired, footer, ...rest }) {
    const { t } = useTranslation()

    const Component = as

    return (
        <div className="creative-composer-section-wrapper">
            <InfoBox {...tooltip}>
                <label className="fs-18 d-inline-block">
                    <>
                        {t(`Recommendations.campaign.setup.${fieldName}.title`)}
                        {isRequired && <span style={{ color: 'red' }}>*</span>}
                    </>
                </label>
            </InfoBox>
            <Component
                id={fieldName}
                placeholder={t(`Recommendations.campaign.setup.${fieldName}.placeholder`)}
                name={fieldName}
                onChange={(e) => onChange(e?.target?.value ?? e)}
                showMax={true}
                {...rest}
            />
            {footer}
        </div>
    )
}

Field.propTypes = {
    fieldName: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    footer: PropTypes.node,
    as: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    tooltip: PropTypes.object,
}

function DescriptionField({ form, isCampaignPublished, onChange }) {
    const { t } = useTranslation()
    const description = form.description ?? ''

    return (
        <Field
            fieldName="description"
            maxLength={250}
            value={description}
            onChange={onChange}
            footer={isCampaignPublished ? null :
                <div className="campaign-form__tips--nokeywords">
                    <p className='ml-3'>
                        {t(
                            'Recommendations.campaign.setup.description.description'
                        )}
                    </p>
                </div>
            }
            maxLengthClassNamesFn={(length) =>
                length > 0 && length <= 200
                    ? 'green'
                    : length > 200 && length <= 240 ? 'orange'
                        : length > 240
                            ? 'red'
                            : ''
            }
        />
    )
}

DescriptionField.propTypes = {
    form: PropTypes.object.isRequired,
    isCampaignPublished: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
}

function parseCreatives(setup, tc, eventDate, translator) {
    const creative = setup?.creatives?.find(x => x.tc === tc) ?? {}
    const [descriptionDate, descriptionTime] = moment.utc(eventDate).format('DD.MM.YYYY HH:mm').split(' ')

    return {
        ...creative,
        headline: creative.headline ?? [],
        body: creative.body ?? [],
        media: augmentIncomingMedia(creative.media),
        destination_url: creative.destination_url ?? [],
        call_to_action: creative.call_to_action,
        audience_id: creative.audience_id,
        description:
            creative.description
            ||
            translator('Recommendations.campaign.setup.description.default', { date: descriptionDate, time: descriptionTime }),
    }
}

export default function CampaignForm({
    eventId,
    eventDate,
    campaign,
    goal,
    setup,
    onAfterSave,
    setMediaLoading,
    onCancel,
}) {
    const user = useSelector(store => store.user)
    const { t } = useTranslation()
    const autosaveTimeout = useRef(null)

    const internalSetup = setup ?? useContext(SetupContext)
    const dispatchCreativesAction = useContext(CreativesDispatchContext)

    const partnerDetails = localStorage.getItem('partnerDetails') && JSON.parse(localStorage.getItem('partnerDetails'))
    const isITCRecommendations = (['ITC', 'ITC_v2']).includes(partnerDetails && partnerDetails.campaignRecommendationsModel)

    const errorRef = useRef(null)

    const [ctaOptions, dispatchCtaOptionsAction] = useReducer(arrayReducer, initStateArray)

    const [userReports, userReportsDispatch] = useReducer(objectReducer, initStateObject)
    const [audiences, dispatchAudiencesAction] = useReducer(arrayReducer, initStateArray)
    const [isAudiencesFetched, setIsAudiencesFetched] = useState(false)

    const originalCreative = useMemo(() => {
        return parseCreatives(internalSetup, campaign.tc, eventDate, t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalSetup, campaign.tc, eventDate])

    const [form, dispatchFormAction] =
        useReducer(creativeFormReducer, { ...initStateObject, content: originalCreative })

    useEffect(() => {
        getUserReports(userReportsDispatch, eventId, { 
            reportType: 'WRONG_TOPIC_RECOMMENDATION',
            tc: campaign.tc,
            tc_run_id: campaign.tc_run_id
        })
    }, [eventId, campaign])

    const reportedKeywords = useMemo(() => {
        if(userReports.content?.items) {
            return userReports.content?.items.map(item => item.keyword)
        }
    }, [userReports])

    useEffect(() => {
        const controller = new AbortController()
        fetchCampaignCtasAsync(dispatchCtaOptionsAction, internalSetup.goal ?? goal, controller)

        return () => controller.abort()
    }, [internalSetup.goal, goal])

    useEffect(() => {
        dispatchFormAction({ type: OVERWRITE, payload: originalCreative })
    }, [originalCreative])
        
    const keywords = useMemo(() => {
        if (isCampaignPublished(campaign)) return {}
        return campaignKeywords(campaign)
    }, [campaign])

    useEffect(() => {
        if (form.error && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [form.error, errorRef])

    // when the component mounts this sets mobile nav to display none
    // the setup started buttons were not showing above the navbar on some browser/ devices 
    useEffect(() => {
        const mobileNav = document.getElementById('mobile-navbar')
        if (mobileNav?.style) {
            mobileNav.style.display = 'none'
        }

        return () => {
            const mobileNav = document.getElementById('mobile-navbar')
            if (mobileNav?.style) {
                mobileNav.style.display = 'block'
            }
        }
    }, [])
    
    const isRunning = isCampaignRunning(campaign, { validateNotEnded: true })
    const isPublished = isCampaignPublished(campaign)
    const isOpen = isCampaignOpen(campaign)

    const onAfterSaveInternal = useCallback((formContent) => {
        if (onAfterSave)
            onAfterSave(formContent)
    }, [onAfterSave])
    
    const submit = useCallback((formContent, e = null) => {
        if (e) e.preventDefault()

        const isRunning = isCampaignRunning(campaign, { validateNotEnded: true })
        if (isCampaignOpen(campaign)) {
            const payload = buildSetupPayload(
                { ...internalSetup, creatives: [...internalSetup.creatives.filter(x => x.tc !== campaign.tc), formContent] },
                { setupOnly: !isRunning, defaultCreatives: { description: formContent.description } },
            )
            dispatchCreativesAction({ type: creativesDispatchActions.loading })

            updateSetupProcess((action) => {
                if (action.type === FAIL) {
                    dispatchCreativesAction({
                        type: creativesDispatchActions.failedUpdate,
                        payload: { tc: campaign.tc, setup: internalSetup, creative: formContent },
                    })
                    dispatchFormAction({ type: FAIL, payload: action.payload })
                } else if (action.type === OVERWRITE) {
                    dispatchCreativesAction({ type: creativesDispatchActions.successfulUpdate, payload: new Date() })
                    dispatchFormAction({ type: creativeActions.idle })
                    onAfterSaveInternal(formContent)
                } else dispatchFormAction(action)
            }, eventId, payload)
        } else {
            const payload = prepareCreativePayload({
                tc: campaign.tc,
                tc_run_id: campaign.tc_run_id,
                ...formContent,
            })

            campaignsSetup((action) => {
                if (action.type === FAIL) dispatchFormAction({ type: FAIL, payload: action.payload })
                else if (action.type === OVERWRITE || action.type === UPDATE) {
                    dispatchFormAction({ type: OVERWRITE, payload: formContent })
                    onAfterSaveInternal(formContent)
                } else dispatchFormAction(action)
            }, eventId, payload)
        }
    }, [eventId, campaign, internalSetup, onAfterSaveInternal, dispatchCreativesAction])

    const onChangeInternal = useCallback((action, form, creatives, isRunning) => {
        dispatchFormAction(action)
        if (isRunning) return

        const newFields = creativeFormReducer(form, action).content

        if (autosaveTimeout.current)
            clearTimeout(autosaveTimeout.current)

        autosaveTimeout.current = setTimeout(() => {
            if (!isEqual(newFields, creatives.find(x => x.tc === campaign.tc)) &&
            newFields.media.filter(file => file.status === fileStatus.uploading).length === 0 &&
                    !hasRestrictedAccessFor(user.accessLevel, 'actions', 'canSaveCampaign')) {
                submit(newFields)
            }
        }, 1000)
    }, [campaign.tc, submit, user.accessLevel])

    const retrieveAudiences = useCallback(() => {
        const adAccountId = (internalSetup.integration_details || []).find(x => x.asset_class === AssetClass.ad_account)?.asset_id
        
        if(!isAudiencesFetched && adAccountId) {
            fetchAudiences(dispatchAudiencesAction, adAccountId, 'custom')
            setIsAudiencesFetched(true)
        }
    }, [internalSetup.integration_details, isAudiencesFetched])

    useEffect(() => {
        retrieveAudiences()

        return () => clearTimeout(autosaveTimeout)
    }, [retrieveAudiences])

    return (
        <>
            <form className="campaign-form width-100" onSubmit={submit} data-testid='campaign-form-test-id'>
                {!!isITCRecommendations &&
                    <Keywords eventId={eventId} setupProcessId={internalSetup?.id} campaign={campaign} isCampaignRunning={isRunning} reportedKeywords={reportedKeywords} keywords={keywords.headline} className="mb-0" />
                }
                {(isOpen || isRunning) && <CoCreate
                    eventId={eventId}
                    tc={form.content.tc}
                    tc_run_id={form.content.tc_run_id}
                    fields={{ bodies: form.content.body, headlines: form.content.headline }}
                    promptForConfirmation={form.content.body.filter(Boolean).length > 0}
                    onChange={(generatedContent) => {
                        onChangeInternal({ type: creativeActions.bodyHeadlineUpdate, payload: generatedContent },
                            form,
                            internalSetup.creatives,
                            isRunning
                        )
                    }}
                    className={`${isITCRecommendations && isOpen ? 'with-border' : ''}`}
                />}

                <div ref={errorRef} data-testid="campaign-form-error-banner">
                    {form.error && <ErrorMessage danger>{t(form.message)}</ErrorMessage>}
                </div>

                <ArrayField
                    as={Textarea}
                    fieldName="body"
                    form={form.content}
                    onChange={(payload, index) =>
                        onChangeInternal({ type: creativeActions.bodyUpdate, index, payload },
                            form,
                            internalSetup.creatives,
                            isRunning
                        )}
                    isAddingEnabled={!isPublished}
                    inputChildren={(index) => index === 0 && (isOpen || isRunning)
                        ?
                        <AutoFill
                            eventId={eventId}
                            tc={form.content.tc}
                            tc_run_id={form.content.tc_run_id}
                            creativeFieldName={generativeCreativeField.bodies}
                            fields={form.content.body}
                            promptForConfirmation={form.content.body.filter(Boolean).length > 0}
                            onChange={(generatedContent) => {
                                onChangeInternal({ type: creativeActions.bodyUpdate, payload: generatedContent },
                                    form,
                                    internalSetup.creatives,
                                    isRunning
                                )
                            }}
                        />
                        : null
                    }
                    restFn={(index) => ({
                        wrapperClass: index === 0 && (isOpen || isRunning) ? 'generative-input align-bottom' : '',
                        inputClass: index === 0 ? 'textarea-autofill' : ''
                    })}
                    maxLength={280}
                    maxLengthClassNamesFn={(length) =>
                        length > 0 && length <= 200
                            ? 'green'
                            : length > 200 && length <= 250 ? 'orange'
                                : length > 250
                                    ? 'red'
                                    : ''
                    }
                    tooltip={{
                        title: t('Recommendations.campaign.setup.body.title'),
                        body:
                            <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                                <p className="pb-2">{t('Tips.Recommendations.CreativeComposer.Body.1')}</p>
                                <p>{t('Tips.Recommendations.CreativeComposer.Body.2')}</p>
                            </div>
                    }}
                >
                    {isITCRecommendations
                        ? null
                        : <Keywords eventId={eventId} setupProcessId={internalSetup?.id} campaign={campaign} isCampaignRunning={isRunning} reportedKeywords={reportedKeywords} keywords={keywords.body} />
                    }
                </ArrayField>
                    
                <ArrayField
                    fieldName="headline"
                    form={form.content}
                    onChange={(payload, index) =>
                        onChangeInternal(
                            { type: creativeActions.headlineUpdate, index, payload },
                            form,
                            internalSetup.creatives,
                            isRunning
                        )
                    }
                    isAddingEnabled={!isPublished}
                    inputChildren={(index) => index === 0 && (isOpen || isRunning)
                        ?
                        <AutoFill
                            eventId={eventId}
                            tc={form.content.tc}
                            tc_run_id={form.content.tc_run_id}
                            creativeFieldName={generativeCreativeField.headlines}
                            fields={form.content.headline}
                            promptForConfirmation={form.content.headline.filter(Boolean).length > 0}
                            onChange={(generatedContent) => {
                                onChangeInternal(
                                    { type: creativeActions.headlineUpdate, payload: generatedContent },
                                    form,
                                    internalSetup.creatives,
                                    isRunning
                                )
                            }}
                        />
                        : null
                    }
                    restFn={(index) => ({
                        wrapperClass: index === 0 && (isOpen || isRunning) ? 'generative-input' : '',
                        inputClass: index === 0 ? 'input-autofill' : ''
                    })}
                    maxLength={80}
                    maxLengthClassNamesFn={(length) =>
                        length > 0 && length <= 40
                            ? 'green'
                            : length > 40 && length <= 60 ? 'orange'
                                : length > 60
                                    ? 'red'
                                    : ''
                    }
                    tooltip={{
                        title: t('Recommendations.campaign.setup.headline.title'),
                        body:
                            <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                                <p className="pb-1">{t('Tips.Recommendations.CreativeComposer.Headline.1')}</p>
                                <p>{t('Tips.Recommendations.CreativeComposer.Headline.2')}</p>
                            </div>
                    }}
                >
                    {isITCRecommendations
                        ? null
                        : <Keywords eventId={eventId} setupProcessId={internalSetup?.id} campaign={campaign} isCampaignRunning={isRunning} reportedKeywords={reportedKeywords} keywords={keywords.headline} className="first"/>
                    }
                </ArrayField>

                <div className="mb-5 creative-composer-section-wrapper creative-composer-section-wrapper__media">
                    <InfoBox title='Media' body={t('Recommendations.campaign.setup.media.info')}>
                        <label className="fs-18 d-inline-block">Media</label>
                    </InfoBox>
                    {!isITCRecommendations && <Keywords eventId={eventId} setupProcessId={internalSetup?.id} campaign={campaign} isCampaignRunning={isRunning} reportedKeywords={reportedKeywords} keywords={keywords.media} className="last" />}
                    <MediaUpload
                        initialMedia={form.content.media}
                        onChange={(files) => {
                            if (setMediaLoading)
                                setMediaLoading(files.some(file => file.status === fileStatus.uploading))

                            onChangeInternal(
                                { type: creativeActions.mediaUpdate, payload: files },
                                form,
                                internalSetup.creatives,
                                isRunning
                            )}}
                        maxItems={10}
                        canAdd={!isPublished}
                        canRemove={!isPublished && (!isRunning || form.content.media.length > 1)}
                        canReplace={isPublished || (isRunning && form.content.media.length === 1)}
                    />
                </div>
                
                <ArrayField
                    fieldName="destination_url"
                    form={form.content}
                    minCount={1}
                    onChange={(payload, index) => onChangeInternal(
                        { type: creativeActions.urlUpdate, index, payload },
                        form,
                        internalSetup.creatives,
                        isRunning
                    )}
                    isAddingEnabled={!isRunning}
                    isRemovingEnabled={!isRunning}
                    removeBtnClassNames="input__remove--r"
                    maxLength={250}
                    maxLengthClassNamesFn={(length) =>
                        length > 0 && length <= 200
                            ? 'green'
                            : length > 200 && length <= 240 ? 'orange'
                                : length > 240
                                    ? 'red'
                                    : ''
                    }
                    validator={(val) => { 
                        return { 
                            isValid: val && constants.URL_VALIDATION_REGEX.test(val), 
                            errorMsg: t('common.errors.wrongUrlFormat')
                        }
                    }}
                    showPositiveFeedback={!isRunning && !isPublished}
                    placeholder="https://www.your-website.com"
                    validateOnBlur
                    tooltip={{
                        title: t('Tips.Recommendations.CreativeComposer.DestinationUrl.title'),
                        body:
                            <ol style={{ listStyle: 'decimal', paddingLeft: '1.4rem',  fontSize: '.875rem', fontWeight: 500 }}>
                                <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.1')}</li>
                                <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.2')}</li>
                                <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.3')}</li>
                            </ol>
                    }}
                    wrapperClass="input-cell--input-padding-max"
                />

                <div className="creative-composer-section-wrapper mb-5">
                    <Field
                        fieldName="call_to_action"
                        as={DropDown}
                        tooltip={{ content: 'Tips.Recommendations.CreativeComposer.CallToAction' }}
                        value={form.content.call_to_action ?? ''}
                        options={ctaOptions.items.map(x => ({ value: x }))}
                        onChange={(payload) => onChangeInternal({ type: creativeActions.callToActionUpdate, payload }, form,
                            internalSetup.creatives,
                            isRunning)}
                        required={false} // For the dropdown. Otherwise a '*' is added when value is empty.
                        classNames="cta-dropdown"
                        disabled={isPublished}
                        renderItem={(item) => {
                            const option = ctaOptions.items.find(x => x === item?.value) ??
                                ctaOptions.items.find(() => true) // fallback first cta option

                            return option ?
                                t([`Recommendations.campaign.setup.call_to_action.${option}`, snakeCaseToPascalCase(option)])
                                : '_'
                        }}
                    />
                </div>

                <div className="creative-composer-section-wrapper mb-5">
                    <Field 
                        fieldName='audience'
                        as={SearchDropdownMultiSelect}
                        tooltip={{ content: 'Tips.Recommendations.CreativeComposer.Audience' }}
                        value={form.content?.audience_id}
                        defaultValue={t('Recommendations.campaign.recommendationTitle', {
                            tc: tcDisplayName(campaign),
                        })}
                        onChange={(payload, index) => onChangeInternal(
                            { type: creativeActions.audienceUpdate, index, payload: payload },
                            form,
                            internalSetup.creatives,
                            isRunning
                        )}
                        isRequired={false}
                        disabled={isPublished}
                        options={audiences.items}
                        error={audiences.error ? getErrorText(audiences.message) : null}
                        loading={audiences.loading}
                        onErrorBtnClick={retrieveAudiences}
                        dropdownClasses='audience'
                    />
                </div>

                <div>
                    <DescriptionField
                        form={form.content}
                        isCampaignPublished={isPublished}
                        onChange={(payload) => onChangeInternal(
                            { type: creativeActions.descriptionUpdate, payload },
                            form,
                            internalSetup.creatives,
                            isRunning
                        )}
                    />
                </div>
                
                <div className="my-3">
                    {form.loading ? (
                        <LoadingSpinner data-testid='loading-spinner' title={t('common.sending')} />
                    ) : (
                        <div className="creative-composer-controls-container">
                            {isRunning ? (
                                <>
                                    <TransparentBgButton
                                        text={t('common.cancel')}
                                        onClick={() => {
                                            dispatchFormAction({
                                                type: OVERWRITE,
                                                payload: { ...initStateObject, content: originalCreative }
                                            })
                                            if (onCancel) onCancel()
                                        }}
                                        color='navy'
                                    />
                                    <SecondaryButton
                                        data-testid='save-campaign-form-btn'
                                        text={t('common.update')}
                                        onClick={(e) => submit(form.content, e)}
                                        color='navy'
                                        disabled={
                                            form.loading ||
                                                !allFilesUploaded(form.content.media) ||
                                                !validateCreative(form.content, { setupOnly: false }).valid ||
                                                hasRestrictedAccessFor(user.accessLevel, 'actions', 'canSaveCampaign')
                                        }
                                    />
                                </>
                            ) : null
                            }
                        </div>
                    )}
                </div>
            </form >
        </>
    )
}

CampaignForm.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eventDate: PropTypes.string.isRequired,
    campaign: PropTypes.object.isRequired,
    goal: PropTypes.string,
    setup: PropTypes.object,
    onAfterSave: PropTypes.func,
    onCancel: PropTypes.func,
    setMediaLoading: PropTypes.func,
}
